import React, { useRef, useMemo } from 'react';

import classNames from 'classnames';

import './Select.scoped.scss';

// Reusable select component. Does not extend select
// Example:

//  <Select
//    maxHeight={200}           // Max height in pixels for dropdown to take
//    disabled                  // Disables the select
//    options={types}           //options (ex: [{id: 'uniqueString', label: "First option"}])
//    value={type}              // Id of currently selected option
//    onChange={value => {      // select handler, supplies id of selected element
//       setType(value);
//    }}
//  />

interface ISelectOption {
  id: string;
  label: string;
  [key: string]: any;
}

interface Props {
  options: ISelectOption[];
  value: string;
  disabled?: boolean;
  onChange: (id: string) => any;
  placeholder?: string;
  startIcon?: React.ReactNode;
  maxHeight?: number;
}

const Select: React.FC<Props> = ({
  value,
  options,
  onChange,
  placeholder = '',
  startIcon = null,
  maxHeight = 400,
  disabled = false,
}) => {
  const containerNode = useRef(null);
  const selectedOption = useMemo(() => {
    const option = options.find(({ id }) => id === value);

    return option || null;
  }, [value, options]); // If this comparison will start failing - we have to redo this to something more expensive

  const filteredOptions = useMemo(() => options.filter(({ id }) => id !== value), [value, options]);

  const handleSelection = id => () => {
    onChange(id);

    if (containerNode.current) {
      containerNode.current.blur();
    }
  };

  return (
    <div
      ref={containerNode}
      tabIndex={-1}
      style={{ '--max-height': maxHeight } as React.CSSProperties}
      className={classNames({ 'custom-select': true, start: startIcon, disabled })}
    >
      <div className="wrapper">
        <div className="value">
          {startIcon && <div className="icon-container start">{startIcon}</div>}
          <div className="content">
            {selectedOption ? (
              selectedOption.label
            ) : (
              <span className="placeholder">{placeholder}</span>
            )}
          </div>

          <div className="arrow"></div>
        </div>

        <div className="dropdown">
          {filteredOptions.map(({ id, label }) => (
            <div key={id} className="option" onClick={handleSelection(id)}>
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Select;
